import type { ComponentType, ReactNode } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { passesAccess } from "@/lib/access";
import { ShiftRequiredRoute } from "./ShiftRequiredRoute";
import { FullPageLoader } from "./FullPageLoader";
import type { RouteAccess } from "@/router/types";

/**
 * Props RouteGuard passes to whatever shift-gate component it's given —
 * `ShiftRequiredRoute`'s own signature, narrowed to what RouteGuard actually
 * supplies (it never passes `facilityId`; direct callers elsewhere do).
 */
export interface ShiftGateProps {
  pageName?: string;
  children: ReactNode;
}

interface RouteGuardProps {
  access?: RouteAccess;
  children: ReactNode;
  /**
   * Renders the shift-check-in gate for `access.shiftRequired` routes.
   * Defaults to `ShiftRequiredRoute`, this app's real one — injectable so
   * RouteGuard doesn't hard-depend on the group-home shift domain
   * (`useFacilityContext`) at the type level, which is what would block
   * moving RouteGuard to @livingsyncs/ui once apps/admin and apps/auditor
   * exist as separate repos that never declare `shiftRequired`.
   *
   * NOT moved to @livingsyncs/ui yet despite this injection point being
   * ready (frontend split epic, Phase 2): RouteGuard's own `useAuth()` call
   * has the SAME mock-reachability problem as useCan.ts — moving it would
   * make it resolve to packages/ui's own useAuth.ts instead of this
   * frontend's mockable `@/hooks/useAuth` shim, breaking every test that
   * mocks `@/hooks/useAuth` around something rendered through the real
   * router (confirmed: 4 tests in buildRouteTree.test.tsx alone). Revisit
   * in Phase 7 alongside useCan/useIdleTimer.
   */
  shiftGate?: ComponentType<ShiftGateProps>;
}

/**
 * Per-route access guard (US-044 AC9). Reads the route's declarative `access`:
 *
 *   anyOf / allOf  → implication-aware permission check (else → /no-access)
 *   shiftRequired  → delegate to the injected shift-gate component
 *
 * (A `grantRequired` → AuditorFacilityRoute delegate existed but no route ever
 * set it — auditor grant enforcement is server-side, `withAuth`'s
 * `auditorFacility` resolver — so the dead client twin was removed.)
 *
 * Loading-state safety (AC16): a permission check renders a loader and makes no
 * decision while `useAuth` is unresolved. A route with **no** `access` needs no
 * context, so it renders its children immediately (no loader flash on load).
 * The shift delegate owns its own loading state.
 */
export function RouteGuard({
  access,
  children,
  shiftGate: ShiftGate = ShiftRequiredRoute,
}: RouteGuardProps) {
  const { data: context, isLoading } = useAuth();

  // Shift gate is a self-contained wrapper (owns its modal + loading).
  if (access?.shiftRequired) {
    return (
      <ShiftGate pageName={access.shiftPageName}>
        {applyPermissionGate(access, context, isLoading, children)}
      </ShiftGate>
    );
  }

  return <>{applyPermissionGate(access, context, isLoading, children)}</>;
}

function applyPermissionGate(
  access: RouteAccess | undefined,
  context: ReturnType<typeof useAuth>["data"],
  isLoading: boolean,
  children: ReactNode,
): ReactNode {
  const needsPermissionDecision = !!(
    access?.anyOf ||
    access?.allOf ||
    access?.resource
  );

  if (needsPermissionDecision) {
    // AC16: no access decision until the context resolves.
    if (isLoading || !context) {
      return <FullPageLoader />;
    }
    // US-009: one shared static-permission predicate (resource→:read, anyOf,
    // allOf) — the same `passesAccess` used by buildNav and useSurface.
    if (!passesAccess({ permissions: context.permissions }, access)) {
      // B-H3: a forbidden route used to silently bounce to the dashboard with no
      // explanation (and `replace`, so Back didn't return). Send the user to the
      // dedicated /no-access page (a top-level route, outside the portal trees —
      // hence a literal, like RoleBasedRedirect/PortalBoundary) so they
      // understand they lack permission rather than thinking the link was broken.
      // F-051 — carry WHAT was required. /no-access used to state
      // unconditionally that the account had no role assigned, because it had
      // nothing else to say; that copy was shown to users who plainly had one.
      // The guard is the only place that knows the requirement, so it forwards
      // it rather than leaving the page to guess the more alarming reason.
      return (
        <Navigate
          to="/no-access"
          replace
          state={{
            requiredPermissions: [
              ...(access?.anyOf ?? []),
              ...(access?.allOf ?? []),
              ...(access?.resource ? [`${access.resource}:read`] : []),
            ],
            from: window.location.pathname,
          }}
        />
      );
    }
  }

  return <>{children}</>;
}
